import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	BalanceDepositType,
	balanceSteps,
	balanceStepsStatus,
	DigitalBalanceFeatureState,
	digitalBalanceSteps,
} from 'constants/balance.constants';
import { intialMessage, userMessage } from 'constants/ui.constants';
import _ from 'lodash';

interface step {
	status: balanceStepsStatus;
}

type steps = {
	[key in balanceSteps]: step;
};

interface errorPopupState {
	open: boolean;
	error: any | null;
	retryData: any | null;
}

interface balanceState {
	loading: boolean;
	message: userMessage;
	activeStep: balanceSteps;
	steps: Partial<steps>;
	companyBankAccount: number | null;
	transferAmount: string | null;
	altshulerBankAccount: number | null;
	suggestedAccountId: number | null;
	balances: any;
	withdrawalSuccess: boolean;
	createdBalance: boolean;
	activeBalance: boolean;
	addBalanceState: string;
	digitalBalanceFeatureState: DigitalBalanceFeatureState;
	errorPopup: errorPopupState;
	showMobilePopup: boolean;
	showComputerPopup: boolean;
	mobileRetryCount: number;
}

const intialStep = { status: balanceStepsStatus.NotStarted };

const intialSteps = () => {
	let s: Partial<steps> = {};
	_.values(balanceSteps).forEach((k) => {
		s[k] = intialStep;
	});
	return s;
};

const initialState: balanceState = {
	loading: false,
	message: intialMessage,
	activeStep: balanceSteps.BankAccount,
	steps: intialSteps(),
	companyBankAccount: null,
	transferAmount: null,
	altshulerBankAccount: null,
	suggestedAccountId: null,
	balances: [],
	withdrawalSuccess: false,
	createdBalance: false,
	activeBalance: false,
	addBalanceState: 'dashboard',
	digitalBalanceFeatureState: DigitalBalanceFeatureState.Enabled,
	errorPopup: {
		open: false,
		error: null,
		retryData: null,
	},
	showMobilePopup: false,
	showComputerPopup: false,
	mobileRetryCount: 0,
};

export const balanceSlice = createSlice({
	name: 'balance',
	initialState,
	reducers: {
		setCompanyBankAccount: (state, action: PayloadAction<number>) => {
			state.companyBankAccount = action.payload;
		},
		setTransferAmount: (state, action: PayloadAction<string>) => {
			state.transferAmount = action.payload;
		},
		setAltshulerBankAccount: (state, action: PayloadAction<number>) => {
			state.altshulerBankAccount = action.payload;
		},
		resetAddBalanceProcess: (state) => {
			state.loading = false;
			state.message = intialMessage;
			state.activeStep = balanceSteps.BankAccount;
			state.steps = intialSteps();
			state.companyBankAccount = null;
			state.transferAmount = null;
			state.altshulerBankAccount = null;
			state.suggestedAccountId = null;
			state.addBalanceState = 'dashboard';
			state.errorPopup = {
				open: false,
				error: null,
				retryData: null,
			};
			state.showMobilePopup = false;
			state.showComputerPopup = false;
			state.mobileRetryCount = 0;
		},
		setLoading: (state, action: PayloadAction<boolean>) => {
			state.loading = action.payload;
		},
		setMessage: (state, action: PayloadAction<userMessage>) => {
			state.message = action.payload;
		},
		setSteps: (state, action: PayloadAction<Partial<steps>>) => {
			state.steps = { ...state.steps, ...action.payload };
		},
		setActiveStep: (state, action: PayloadAction<balanceSteps>) => {
			if (action.payload === balanceSteps.Complete && !state.createdBalance) {
				state.createdBalance = true;
			}
			state.activeStep = action.payload;
		},
		setSuggestedAccountId: (state, action: PayloadAction<number | null>) => {
			state.suggestedAccountId = action.payload;
		},
		setCompanyBalances: (state, { payload }) => {
			state.balances = payload;
		},
		setWithdrawalSuccess: (state, action: PayloadAction<boolean>) => {
			state.withdrawalSuccess = action.payload;
		},
		setAddBalanceState: (state, action: PayloadAction<string>) => {
			state.addBalanceState = action.payload;
			state.activeStep = balanceSteps.BankAccount;
			state.steps = intialSteps();
			state.companyBankAccount = null;
			state.transferAmount = null;
			state.altshulerBankAccount = null;
		},
		setDigitalBalanceFeatureState: (state, action: PayloadAction<DigitalBalanceFeatureState>) => {
			state.digitalBalanceFeatureState = action.payload;
		},
		showErrorPopup: (state, action: PayloadAction<{ error: any; retryData?: any }>) => {
			state.errorPopup = {
				open: true,
				error: action.payload.error,
				retryData: action.payload.retryData || null,
			};
		},
		hideErrorPopup: (state) => {
			state.errorPopup = {
				open: false,
				error: null,
				retryData: null,
			};
		},
		showMobilePopup: (state) => {
			state.showMobilePopup = true;
		},
		hideMobilePopup: (state) => {
			state.showMobilePopup = false;
		},
		showComputerPopup: (state) => {
			state.showComputerPopup = true;
		},
		hideComputerPopup: (state) => {
			state.showComputerPopup = false;
		},
		incrementMobileRetryCount: (state) => {
			state.mobileRetryCount += 1;
		},
		resetMobileRetryCount: (state) => {
			state.mobileRetryCount = 0;
		},
	},
});

export const {
	setLoading,
	setMessage,
	setSteps,
	setActiveStep,
	setTransferAmount,
	resetAddBalanceProcess,
	setCompanyBankAccount,
	setAltshulerBankAccount,
	setSuggestedAccountId,
	setCompanyBalances,
	setWithdrawalSuccess,
	setAddBalanceState,
	setDigitalBalanceFeatureState,
	showErrorPopup,
	hideErrorPopup,
	showMobilePopup,
	hideMobilePopup,
	showComputerPopup,
	hideComputerPopup,
	incrementMobileRetryCount,
	resetMobileRetryCount,
} = balanceSlice.actions;

export const isLoading = (state: any) => state.balance.loading;
export const getMessage = (state: any) => state.balance.message;
export const getSteps = (state: any) => {
	const balanceState = getAddBalanceState(state);

	if (balanceState === BalanceDepositType.Digital) {
		let digitalSteps = {};
		_.values(digitalBalanceSteps).forEach((step) => {
			digitalSteps[step] = { status: balanceStepsStatus.NotStarted };
		});
		return digitalSteps;
	}
	return state.balance.steps;
};

export const getDigitalSteps = (state: any) => {
	return getSteps(state);
};

export const getActiveStep = (state: any) => {
	const balanceState = getAddBalanceState(state);
	const stepsArray =
		balanceState === BalanceDepositType.Digital ? _.values(digitalBalanceSteps) : _.values(balanceSteps);
	return _.indexOf(stepsArray, state.balance.activeStep);
};

export const getTotalSteps = (state: any) => {
	const balanceState = getAddBalanceState(state);
	return balanceState === BalanceDepositType.Digital
		? _.values(digitalBalanceSteps).length
		: _.values(balanceSteps).length;
};

export const getCompanyBankAccount = (state: any) => state.balance.companyBankAccount;
export const getAltshulerBankAccount = (state: any) => state.balance.altshulerBankAccount;
export const getTranfserAmount = (state: any) => state.balance.transferAmount;
export const getCompanyBalances = (state: any) => state.balance.balances;
export const getSuggestedAccountId = (state: any) => state.balance.suggestedAccountId;
export const getWithdrawalSuccess = (state: any) => state.balance.withdrawalSuccess;
export const getAddBalanceState = (state: any) => state.balance.addBalanceState;
export const getDigitalBalanceFeatureState = (state: any) => state.balance.digitalBalanceFeatureState;
export const getErrorPopup = (state: any) => state.balance.errorPopup;
export const getShowMobilePopup = (state: any) => state.balance.showMobilePopup;
export const getShowComputerPopup = (state: any) => state.balance.showComputerPopup;
export const getMobileRetryCount = (state: any) => state.balance.mobileRetryCount;
export default balanceSlice.reducer;
